import { useState, useEffect, useCallback, useRef } from 'react';
import Lottie from 'lottie-react';
import { promotionBannerAPI, uploadAPI, BASE_URL } from '../services/api';
import Icon from '../components/Icon';

// Convert a server-relative upload path (`/uploads/...`) into an absolute URL
// using the SAME host the rest of the admin talks to. Absolute URLs pass through.
const toAbsolute = (u) => {
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('/')) return `${BASE_URL}${u}`;
  return u;
};

// Screen / placement registry — must match server's PLACEMENTS map.
const SCREEN_LABELS = {
  home: 'Home Screen',
  exclusive_service: 'Exclusive Service Screen',
  vehicle_details: 'Vehicle Details Screen',
  resale_value: 'Resale Value Screen',
  search: 'Search Screen',
  services: 'Services Screen',
};
const PLACEMENT_LABELS = {
  search_vehicle: 'Search Vehicle',
  search_challan: 'Search Challan',
  banner_list: 'Banner List',
  pending_challan: 'Pending Challan',
  personal_loan_small: 'Personal Loan (Small)',
  personal_loan_big: 'Personal Loan (Big)',
  sell_car_banner: 'Sell Car Banner',
  lottie_bike_insurance: 'Lottie — Bike Insurance',
  lottie_car_insurance: 'Lottie — Car Insurance',
  lottie_check_challan: 'Lottie — Check Challan',
  lottie_sell_your_car: 'Lottie — Sell Your Car',
  car_service_report: 'Get Car Service Report',
  loan_instant: 'Get Loan Instantly',
  pre_delivery_inspection: 'Pre-Delivery Inspections',
  cash_loan: 'Cash Loan',
  pollution_certificate: 'Pollution Certificate',
  road_tax_payment: 'Road Tax Payment',
  vehicle_loan: 'Vehicle Loan',
  emergency_assistance: 'Emergency Assistance',
};
const PLACEMENTS = {
  home: ['search_vehicle', 'search_challan'],
  exclusive_service: ['banner_list'],
  vehicle_details: ['pending_challan', 'personal_loan_small', 'personal_loan_big'],
  resale_value: ['sell_car_banner'],
  search: ['lottie_bike_insurance', 'lottie_car_insurance', 'lottie_check_challan', 'lottie_sell_your_car'],
  services: [
    'car_service_report', 'loan_instant',
    'pre_delivery_inspection', 'cash_loan',
    'pollution_certificate', 'road_tax_payment',
    'vehicle_loan', 'emergency_assistance',
  ],
};
const isLottiePlacement = (p) => p && p.startsWith('lottie_');

const styles = {
  container: { width: '100%' },
  header: { marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
  card: { backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderBottom: '1px solid #e2e8f0', gap: '12px', flexWrap: 'wrap' },
  filters: { display: 'flex', gap: '8px', flexWrap: 'wrap', flex: 1 },
  select: { padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#fff' },
  searchInput: { flex: 1, minWidth: '180px', maxWidth: '300px', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
  addButton: { padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '14px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#1e293b', verticalAlign: 'top' },
  thumb: { width: '64px', height: '40px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' },
  pillBlue: { fontFamily: 'Monaco, Consolas, monospace', fontSize: '12px', color: '#6366f1', backgroundColor: '#eef2ff', padding: '3px 8px', borderRadius: '4px' },
  pillSlate: { fontSize: '12px', color: '#475569', backgroundColor: '#f1f5f9', padding: '3px 8px', borderRadius: '4px' },
  url: { fontSize: '13px', color: '#3b82f6', wordBreak: 'break-all', maxWidth: '260px', display: 'inline-block' },
  statusBadge: { padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase' },
  active: { backgroundColor: '#dcfce7', color: '#166534' },
  inactive: { backgroundColor: '#fee2e2', color: '#991b1b' },
  actions: { display: 'flex', gap: '8px' },
  iconBtn: { padding: '8px', border: 'none', borderRadius: '6px', backgroundColor: 'transparent', cursor: 'pointer', color: '#64748b' },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#64748b' },
  modal: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalBox: { backgroundColor: '#fff', borderRadius: '16px', width: '92%', maxWidth: '640px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  modalHeader: { padding: '18px 22px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle: { fontSize: '17px', fontWeight: '700', color: '#1e293b' },
  modalBody: { padding: '20px 22px', overflowY: 'auto' },
  modalFooter: { padding: '14px 22px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
  formRow: { marginBottom: '14px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', minHeight: '120px', fontFamily: 'Monaco, Consolas, monospace' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  btn: { padding: '10px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', border: 'none' },
  btnPrimary: { backgroundColor: '#4f46e5', color: '#fff' },
  btnSecondary: { backgroundColor: '#f1f5f9', color: '#475569' },
  errorBox: { padding: '10px 14px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', marginBottom: '12px', fontSize: '13px' },
};

// Renders a Lottie animation from a JSON string (handles plain + double-encoded).
// Returns a fallback placeholder if the JSON is invalid.
function LottiePreview({ json, style, fallbackLabel = 'invalid Lottie' }) {
  if (!json) return null;
  let data = null;
  try {
    let v = JSON.parse(json);
    if (typeof v === 'string') { try { v = JSON.parse(v); } catch {} }
    if (v && typeof v === 'object') data = v;
  } catch {}
  if (!data) {
    return (
      <div style={{
        ...style,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#fef2f2', color: '#dc2626', fontSize: 11, fontWeight: 600,
        border: '1px dashed #fecaca', borderRadius: 8,
      }}>
        <Icon name="warning" size={13} style={{ marginRight: 5 }} />{fallbackLabel}
      </div>
    );
  }
  return <Lottie animationData={data} loop autoplay style={style} />;
}

const blankForm = {
  screen: 'home',
  placement: 'search_vehicle',
  title: '',
  imageUrl: '',
  clickUrl: '',
  lottieJson: '',
  sortOrder: 0,
  isActive: true,
};

export default function PromotionBanners() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [screenFilter, setScreenFilter] = useState('');
  const [placementFilter, setPlacementFilter] = useState('');
  const [view, setView] = useState('list'); // 'list' | 'preview'
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 200 };
      if (screenFilter) params.screen = screenFilter;
      if (placementFilter) params.placement = placementFilter;
      if (search.trim()) params.search = search.trim();
      const res = await promotionBannerAPI.getAll(params);
      setItems(res.data?.data || []);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load promotion banners');
    } finally {
      setLoading(false);
    }
  }, [search, screenFilter, placementFilter]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(blankForm);
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      screen: item.screen,
      placement: item.placement,
      title: item.title || '',
      imageUrl: item.imageUrl || '',
      clickUrl: item.clickUrl || '',
      lottieJson: item.lottieJson || '',
      sortOrder: item.sortOrder ?? 0,
      isActive: !!item.isActive,
    });
    setFormError('');
    setShowModal(true);
  };

  const onScreenChange = (s) => {
    const firstPlacement = (PLACEMENTS[s] || [])[0] || '';
    setForm((f) => ({ ...f, screen: s, placement: firstPlacement }));
  };

  const save = async () => {
    if (!form.screen || !form.placement) { setFormError('Screen and placement are required'); return; }
    if (isLottiePlacement(form.placement)) {
      if (!form.lottieJson.trim()) { setFormError('Lottie JSON is required for lottie placements'); return; }
    } else if (!form.imageUrl.trim()) {
      setFormError('Image URL is required'); return;
    }
    try {
      setSaving(true);
      // Auto-generated title for admin reference (DB requires non-null).
      const autoTitle = `${SCREEN_LABELS[form.screen] || form.screen} — ${PLACEMENT_LABELS[form.placement] || form.placement}`;
      const payload = {
        screen: form.screen,
        placement: form.placement,
        title: (form.title?.trim() || autoTitle),
        imageUrl: form.imageUrl.trim() || null,
        clickUrl: form.clickUrl.trim() || null,
        lottieJson: form.lottieJson.trim() || null,
        sortOrder: Number(form.sortOrder) || 0,
        isActive: !!form.isActive,
      };
      if (editing) await promotionBannerAPI.update(editing.id, payload);
      else await promotionBannerAPI.create(payload);
      setShowModal(false);
      load();
    } catch (e) {
      setFormError(e?.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await promotionBannerAPI.delete(item.id);
      load();
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to delete');
    }
  };

  const placementsForScreen = (s) => (s ? PLACEMENTS[s] || [] : []);

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFormError('Only image files are allowed');
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setFormError('Image is too large. Maximum 5 MB.');
      e.target.value = '';
      return;
    }
    try {
      setUploading(true);
      setFormError('');
      const res = await uploadAPI.uploadBannerImage(file);
      // Prefer the relative path (`/uploads/banners/...`) — it's portable across
      // dev/staging/prod. Fall back to the absolute `url` if relativeUrl isn't sent.
      const url = res.data?.relativeUrl || res.data?.url;
      if (url) {
        setForm((f) => ({ ...f, imageUrl: url }));
      } else {
        setFormError('Upload succeeded but no URL returned');
      }
    } catch (err) {
      setFormError(err?.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const clearImage = () => setForm((f) => ({ ...f, imageUrl: '' }));

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Promotion Banners</h1>
          <p style={styles.subtitle}>Banners and Lottie animations served to mobile app screens.</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* List | Preview toggle */}
        <div style={{
          display: 'inline-flex', background: '#f1f5f9', borderRadius: 8, padding: 3,
        }}>
          <button
            onClick={() => setView('list')}
            style={{
              padding: '7px 14px', border: 'none', borderRadius: 6, fontSize: 13,
              fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center',
              background: view === 'list' ? '#fff' : 'transparent',
              color: view === 'list' ? '#1e293b' : '#64748b',
              boxShadow: view === 'list' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
            }}
          ><Icon name="list" size={14} style={{ marginRight: 6 }} />List</button>
          <button
            onClick={() => setView('preview')}
            style={{
              padding: '7px 14px', border: 'none', borderRadius: 6, fontSize: 13,
              fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center',
              background: view === 'preview' ? '#fff' : 'transparent',
              color: view === 'preview' ? '#1e293b' : '#64748b',
              boxShadow: view === 'preview' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
            }}
          ><Icon name="mobile" size={14} style={{ marginRight: 6 }} />Screen Preview</button>
        </div>
        <input
          style={styles.searchInput}
          placeholder="Search title or click URL…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          style={styles.select}
          value={screenFilter}
          onChange={(e) => { setScreenFilter(e.target.value); setPlacementFilter(''); }}
        >
          <option value="">All Screens</option>
          {Object.keys(PLACEMENTS).map(s => (
            <option key={s} value={s}>{SCREEN_LABELS[s] || s}</option>
          ))}
        </select>
        <button style={styles.addButton} onClick={openCreate}>+ Add Banner</button>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {loading ? (
        <div style={styles.card}><div style={styles.empty}>Loading…</div></div>
      ) : view === 'preview' ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
          gap: 36,
          justifyItems: 'center',
        }}>
          {Object.keys(PLACEMENTS)
            .filter(s => !screenFilter || s === screenFilter)
            .map(screen => {
              const screenItems = items.filter(i => i.screen === screen);
              return (
                <div key={screen} style={{
                  /* Outer device body — matt-black bezel with rounded corners */
                  position: 'relative',
                  width: 320,
                  height: 640,
                  background: '#1c1c1e',
                  borderRadius: 44,
                  padding: 12,
                  boxShadow:
                    '0 1px 0 rgba(255,255,255,0.08) inset,' +
                    '0 0 0 2px #2c2c2e,' +
                    '0 30px 60px rgba(0,0,0,0.25)',
                }}>
                  {/* Side buttons */}
                  <span style={{ position: 'absolute', top: 110, left: -3, width: 3, height: 32, background: '#2c2c2e', borderRadius: 2 }} />
                  <span style={{ position: 'absolute', top: 160, left: -3, width: 3, height: 56, background: '#2c2c2e', borderRadius: 2 }} />
                  <span style={{ position: 'absolute', top: 230, left: -3, width: 3, height: 56, background: '#2c2c2e', borderRadius: 2 }} />
                  <span style={{ position: 'absolute', top: 140, right: -3, width: 3, height: 88, background: '#2c2c2e', borderRadius: 2 }} />

                  {/* Inner screen */}
                  <div style={{
                    position: 'relative',
                    width: '100%', height: '100%',
                    background: '#f8fafc',
                    borderRadius: 32,
                    overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                  }}>
                    {/* Notch / dynamic island */}
                    <div style={{
                      position: 'absolute', top: 8, left: '50%',
                      transform: 'translateX(-50%)',
                      width: 110, height: 28, background: '#000',
                      borderRadius: 18, zIndex: 5,
                    }} />
                    {/* Status bar */}
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '12px 24px 6px', fontSize: 11, fontWeight: 600,
                      color: '#1e293b', height: 36, position: 'relative', zIndex: 4,
                    }}>
                      <span>9:41</span>
                      <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <span style={{ fontSize: 10 }}>●●●●</span>
                        <span style={{ fontSize: 10 }}>100%</span>
                      </span>
                    </div>
                    {/* App bar */}
                    <div style={{
                      background: '#4f46e5', color: '#fff', padding: '10px 16px',
                      fontSize: 13, fontWeight: 700,
                    }}>
                      {SCREEN_LABELS[screen]}
                    </div>
                    {/* Placements */}
                    <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto', flex: 1 }}>
                      {PLACEMENTS[screen].map(placement => {
                        const placementItems = screenItems
                          .filter(i => i.placement === placement && i.isActive)
                          .sort((a, b) => a.sortOrder - b.sortOrder);
                        const isLottie = placement.startsWith('lottie_');
                        const heightHint =
                          placement === 'personal_loan_big' ? 110 :
                          placement === 'personal_loan_small' ? 70 :
                          placement === 'banner_list' ? 90 :
                          isLottie ? 130 : 90;
                        return (
                          <div key={placement}>
                            <div style={{
                              fontSize: 10, color: '#94a3b8', marginBottom: 4,
                              fontWeight: 600, letterSpacing: 0.3,
                              textTransform: 'uppercase',
                            }}>
                              {PLACEMENT_LABELS[placement]}
                            </div>
                            {placementItems.length === 0 ? (
                              <div style={{
                                height: heightHint, borderRadius: 10,
                                border: '1.5px dashed #cbd5e1', background: '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#94a3b8', fontSize: 11,
                              }}>
                                empty slot
                              </div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {placementItems.map(item => (
                                  <div
                                    key={item.id}
                                    onClick={() => openEdit(item)}
                                    style={{
                                      borderRadius: 10, overflow: 'hidden',
                                      cursor: 'pointer', position: 'relative',
                                      background: '#fff',
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                    }}
                                    title={`Click to edit · ${item.clickUrl || 'no click URL'}`}
                                  >
                                    {item.imageUrl && (
                                      <img
                                        src={toAbsolute(item.imageUrl)}
                                        alt=""
                                        style={{
                                          width: '100%', height: heightHint,
                                          objectFit: 'cover', display: 'block',
                                        }}
                                      />
                                    )}
                                    {item.lottieJson && !item.imageUrl && (
                                      <LottiePreview
                                        json={item.lottieJson}
                                        style={{ width: '100%', height: heightHint, background: '#fff' }}
                                      />
                                    )}
                                    {!item.imageUrl && !item.lottieJson && (
                                      <div style={{
                                        height: heightHint, display: 'flex',
                                        alignItems: 'center', justifyContent: 'center',
                                        color: '#94a3b8', fontSize: 11,
                                      }}>no media</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <button
                        onClick={() => {
                          setEditing(null);
                          setForm({ ...blankForm, screen, placement: PLACEMENTS[screen][0] });
                          setFormError('');
                          setShowModal(true);
                        }}
                        style={{
                          marginTop: 4, padding: '8px',
                          background: '#eef2ff', color: '#4f46e5',
                          border: 'none', borderRadius: 8,
                          fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        }}
                      >+ Add to {SCREEN_LABELS[screen]}</button>
                    </div>
                    {/* Home indicator bar at bottom */}
                    <div style={{
                      position: 'absolute', bottom: 6, left: '50%',
                      transform: 'translateX(-50%)',
                      width: 120, height: 4, background: '#1e293b',
                      borderRadius: 2, opacity: 0.85,
                    }} />
                  </div>
                </div>
              );
            })}
        </div>
      ) : (
        Object.keys(PLACEMENTS)
          .filter(s => !screenFilter || s === screenFilter)
          .map(screen => {
            const screenItems = items.filter(i => i.screen === screen);
            return (
              <div key={screen} style={{ ...styles.card, marginBottom: 24, overflow: 'visible' }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '14px 18px', borderBottom: '1px solid #e2e8f0',
                  background: 'linear-gradient(to right, #eef2ff, #ffffff)',
                  borderRadius: '12px 12px 0 0',
                }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                      {SCREEN_LABELS[screen]}
                    </h3>
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: '#64748b' }}>
                      {PLACEMENTS[screen].length} placement{PLACEMENTS[screen].length === 1 ? '' : 's'} · {screenItems.length} banner{screenItems.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <button
                    style={{ ...styles.btn, ...styles.btnSecondary, padding: '8px 14px', fontSize: 13 }}
                    onClick={() => {
                      setEditing(null);
                      setForm({ ...blankForm, screen, placement: PLACEMENTS[screen][0] });
                      setFormError('');
                      setShowModal(true);
                    }}
                  >
                    + Add to this screen
                  </button>
                </div>
                <div style={{ padding: 16 }}>
                  {PLACEMENTS[screen].map(placement => {
                    const placementItems = screenItems.filter(i => i.placement === placement);
                    return (
                      <div key={placement} style={{ marginBottom: 18 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <span style={{ ...styles.pillBlue, fontSize: 12 }}>
                            {PLACEMENT_LABELS[placement]}
                          </span>
                          <span style={{ fontSize: 12, color: '#94a3b8' }}>
                            {placementItems.length === 0 ? 'empty' : `${placementItems.length} item${placementItems.length === 1 ? '' : 's'}`}
                          </span>
                        </div>
                        {placementItems.length === 0 ? (
                          <button
                            style={{
                              width: '100%', padding: '14px',
                              border: '1.5px dashed #cbd5e1', borderRadius: 8,
                              background: '#f8fafc', color: '#64748b',
                              fontSize: 13, cursor: 'pointer',
                            }}
                            onClick={() => {
                              setEditing(null);
                              setForm({ ...blankForm, screen, placement });
                              setFormError('');
                              setShowModal(true);
                            }}
                          >
                            + Add banner for {PLACEMENT_LABELS[placement]}
                          </button>
                        ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                            {placementItems.map(item => (
                              <div key={item.id} style={{
                                border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden',
                                background: '#fff', display: 'flex', flexDirection: 'column',
                              }}>
                                <div style={{
                                  height: 100, background: '#f8fafc',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  borderBottom: '1px solid #e2e8f0',
                                }}>
                                  {item.imageUrl ? (
                                    <img
                                      src={toAbsolute(item.imageUrl)}
                                      alt=""
                                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                    />
                                  ) : item.lottieJson ? (
                                    <div style={{ textAlign: 'center', color: '#7c3aed' }}>
                                      <div style={{ display: 'flex', justifyContent: 'center' }}><Icon name="video" size={24} /></div>
                                      <div style={{ fontSize: 11, fontWeight: 600 }}>Lottie animation</div>
                                    </div>
                                  ) : (
                                    <span style={{ color: '#cbd5e1', fontSize: 12 }}>no preview</span>
                                  )}
                                </div>
                                <div style={{ padding: 10, flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ ...styles.statusBadge, ...(item.isActive ? styles.active : styles.inactive) }}>
                                      {item.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                    <span style={{ fontSize: 11, color: '#94a3b8' }}>order {item.sortOrder}</span>
                                  </div>
                                  {item.clickUrl && (
                                    <a
                                      href={item.clickUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ fontSize: 11, color: '#3b82f6', wordBreak: 'break-all', textDecoration: 'none' }}
                                    >
                                      {item.clickUrl.length > 50 ? item.clickUrl.slice(0, 50) + '…' : item.clickUrl}
                                    </a>
                                  )}
                                  <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
                                    <button
                                      style={{ ...styles.btn, ...styles.btnSecondary, flex: 1, padding: '6px', fontSize: 12 }}
                                      onClick={() => openEdit(item)}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      style={{ ...styles.btn, ...styles.btnSecondary, padding: '6px 10px', fontSize: 12, color: '#dc2626' }}
                                      onClick={() => remove(item)}
                                    >
                                      <Icon name="trash" size={14} />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
      )}

      {showModal && (
        <div style={styles.modal} onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div style={styles.modalBox}>
            <div style={styles.modalHeader}>
              <span style={styles.modalTitle}>{editing ? 'Edit Banner' : 'Add Banner'}</span>
              <button style={styles.iconBtn} onClick={() => setShowModal(false)}><Icon name="close" size={16} /></button>
            </div>
            <div style={styles.modalBody}>
              {formError && <div style={styles.errorBox}>{formError}</div>}

              <div style={styles.twoCol}>
                <div style={styles.formRow}>
                  <label style={styles.label}>Screen *</label>
                  <select style={styles.input} value={form.screen} onChange={(e) => onScreenChange(e.target.value)}>
                    {Object.keys(PLACEMENTS).map(s => (
                      <option key={s} value={s}>{SCREEN_LABELS[s] || s}</option>
                    ))}
                  </select>
                </div>
                <div style={styles.formRow}>
                  <label style={styles.label}>Placement *</label>
                  <select style={styles.input} value={form.placement} onChange={(e) => setForm(f => ({ ...f, placement: e.target.value }))}>
                    {placementsForScreen(form.screen).map(p => (
                      <option key={p} value={p}>{PLACEMENT_LABELS[p] || p}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={styles.formRow}>
                <label style={styles.label}>Name</label>
                <input
                  type="text"
                  style={styles.input}
                  value={form.title}
                  onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Internal label (auto-generated if blank)"
                  maxLength={200}
                />
              </div>

              {!isLottiePlacement(form.placement) && (
                <>
                  <div style={styles.formRow}>
                    <label style={styles.label}>Banner Image *</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={onPickImage}
                      style={{ display: 'none' }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      {form.imageUrl ? (
                        <>
                          <img
                            src={toAbsolute(form.imageUrl)}
                            alt="preview"
                            style={{ width: '120px', height: '72px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}
                          />
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            style={{ ...styles.btn, ...styles.btnSecondary }}
                          >
                            {uploading ? 'Uploading…' : 'Replace Image'}
                          </button>
                          <button
                            type="button"
                            onClick={clearImage}
                            style={{ ...styles.btn, ...styles.btnSecondary, color: '#dc2626' }}
                          >
                            Remove
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          style={{ ...styles.btn, ...styles.btnPrimary }}
                        >
                          {uploading ? 'Uploading…' : <><Icon name="upload" size={14} style={{ marginRight: 6 }} />Upload Image</>}
                        </button>
                      )}
                    </div>
                    <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '6px' }}>
                      JPG, PNG, GIF, WebP, or SVG · max 5 MB · stored at <code>/uploads/banners/</code>
                    </p>
                    <details style={{ marginTop: '8px' }}>
                      <summary style={{ fontSize: '12px', color: '#64748b', cursor: 'pointer' }}>or paste an external image URL</summary>
                      <input
                        style={{ ...styles.input, marginTop: '8px' }}
                        value={form.imageUrl}
                        onChange={(e) => setForm(f => ({ ...f, imageUrl: e.target.value }))}
                        placeholder="https://…/banner.png"
                      />
                    </details>
                  </div>
                  <div style={styles.formRow}>
                    <label style={styles.label}>Click URL (opens when banner is tapped)</label>
                    <input style={styles.input} value={form.clickUrl} onChange={(e) => setForm(f => ({ ...f, clickUrl: e.target.value }))} placeholder="https://…/landing" />
                  </div>
                </>
              )}

              {isLottiePlacement(form.placement) && (
                <>
                  <div style={styles.formRow}>
                    <label style={styles.label}>Lottie JSON *</label>
                    <textarea
                      style={styles.textarea}
                      value={form.lottieJson}
                      onChange={(e) => setForm(f => ({ ...f, lottieJson: e.target.value }))}
                      placeholder='Paste Lottie JSON here ({"v":"5.7.4","fr":60,"layers":[…]})'
                    />
                  </div>
                  <div style={styles.formRow}>
                    <label style={styles.label}>Click URL (optional — opens when animation is tapped)</label>
                    <input style={styles.input} value={form.clickUrl} onChange={(e) => setForm(f => ({ ...f, clickUrl: e.target.value }))} placeholder="https://…/landing" />
                  </div>
                </>
              )}

              <div style={styles.twoCol}>
                <div style={styles.formRow}>
                  <label style={styles.label}>Sort Order</label>
                  <input type="number" style={styles.input} value={form.sortOrder} onChange={(e) => setForm(f => ({ ...f, sortOrder: e.target.value }))} />
                </div>
                <div style={styles.formRow}>
                  <label style={styles.label}>Status</label>
                  <select style={styles.input} value={form.isActive ? 'true' : 'false'} onChange={(e) => setForm(f => ({ ...f, isActive: e.target.value === 'true' }))}>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={save} disabled={saving}>
                {saving ? 'Saving…' : (editing ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
